import type { SortingState } from '@tanstack/react-table';

import {
  MerchantOrderStatusEnum,
  MerchantOrderTypeEnum,
  OpsOrderCarrier,
  OpsOrderHealthEnum,
  OpsOrderSystem,
  type OpsOrderFilter,
  type OpsOrderSort,
  type SortOrder,
} from '@/graphql/generatedTypes';
import type {
  ActiveFilter,
  FilterDefinition,
} from '@/views/Order/components/table/OrderToolbar';
import { MULTI_VALUE_SEPARATOR } from '@/views/Order/components/table/TableFacetedFilter';
import i18n from '@/utils/i18n';

export type OrderSystemType = 'EVENT' | 'OPEN_SYSTEM';

/** Maps the local system discriminator onto the GraphQL enum. */
export const ORDER_SYSTEM_TO_GQL: Record<OrderSystemType, OpsOrderSystem> = {
  EVENT: OpsOrderSystem.Event,
  OPEN_SYSTEM: OpsOrderSystem.OpenSystem,
};

// ---------------------------------------------------------------------------
// Date-range filter values
// ---------------------------------------------------------------------------

/**
 * Date-range filters persist browser-local CALENDAR days (`YYYY-MM-DD`), not
 * timestamps — picking "March 10" must keep meaning March 10 to the person who
 * picked it, whatever timezone they are in. The local day is only widened into
 * an explicit instant when the query is built (see `buildOrderListFilter`).
 * Either side may be empty for an open-ended range: `2026-03-01..`, `..2026-03-31`.
 */
const DATE_RANGE_SEPARATOR = '..';

export function formatLocalDay(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseLocalDay(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  // Rejects impossible days like 2026-02-31, which the Date constructor rolls
  // over instead of failing.
  return date.getDate() === Number(day) ? date : undefined;
}

/** Serialises a picked range into an `ActiveFilter.value`. */
export function serializeDateRangeValue(range: {
  from?: Date;
  to?: Date;
}): string {
  const from = range.from ? formatLocalDay(range.from) : '';
  const to = range.to ? formatLocalDay(range.to) : '';
  if (!from && !to) return '__all__';
  return `${from}${DATE_RANGE_SEPARATOR}${to}`;
}

/**
 * Parses an `ActiveFilter.value` back into local dates for the picker. Both
 * keys are always present (matching react-day-picker's `DateRange`), either may
 * be undefined.
 */
export function parseDateRangeValue(value: string): {
  from: Date | undefined;
  to: Date | undefined;
} {
  if (!value || value === '__all__') return { from: undefined, to: undefined };
  const [rawFrom = '', rawTo = ''] = value.split(DATE_RANGE_SEPARATOR);
  return { from: parseLocalDay(rawFrom), to: parseLocalDay(rawTo) };
}

/**
 * Widens a local calendar range into the inclusive instants the backend
 * compares against `MerchantOrders.CreatedTimestamp`: local start-of-day for
 * `from`, local end-of-day for `to`.
 */
function dateRangeValueToRangeInput(
  value: string,
): { from?: string; to?: string } | undefined {
  const { from, to } = parseDateRangeValue(value);
  if (!from && !to) return undefined;

  const range: { from?: string; to?: string } = {};
  if (from) {
    range.from = new Date(
      from.getFullYear(),
      from.getMonth(),
      from.getDate(),
      0,
      0,
      0,
      0,
    ).toISOString();
  }
  if (to) {
    range.to = new Date(
      to.getFullYear(),
      to.getMonth(),
      to.getDate(),
      23,
      59,
      59,
      999,
    ).toISOString();
  }
  return range;
}

// ---------------------------------------------------------------------------
// Shared filter fragments
// ---------------------------------------------------------------------------

const INBOUND_ORDER_TYPES = new Set<MerchantOrderTypeEnum>([
  MerchantOrderTypeEnum.Pickup,
  MerchantOrderTypeEnum.Termination,
  MerchantOrderTypeEnum.Inventory,
  MerchantOrderTypeEnum.DpdPickup,
  MerchantOrderTypeEnum.DpdShopReturn,
]);

/** Synthetic value that expands to all onboarding enum variants at query time. */
const ONBOARDING_GROUP = 'ONBOARDING_GROUP';
const ONBOARDING_ENUM_VALUES: MerchantOrderTypeEnum[] = [
  MerchantOrderTypeEnum.Onboarding,
  MerchantOrderTypeEnum.OnboardingS,
  MerchantOrderTypeEnum.OnboardingM,
  MerchantOrderTypeEnum.OnboardingL,
];

const TYPE_FILTER: FilterDefinition = {
  id: 'type',
  getLabel: () => i18n.t('Order:filterType'),
  isDefault: true,
  isRemovable: false,
  multiSelect: true,
  popoverContentClassName: 'tw:w-[240px]',
  options: [
    { type: 'text', getLabel: () => i18n.t('Order:filterSectionOutbound') },
    { getLabel: () => 'Resupply', value: MerchantOrderTypeEnum.Resupply },
    { getLabel: () => 'Onboarding S, M, L', value: ONBOARDING_GROUP },
    { getLabel: () => 'Special', value: MerchantOrderTypeEnum.Special },
    { getLabel: () => 'Sample', value: MerchantOrderTypeEnum.Sample },
    {
      getLabel: () => 'Warehouse Transfer',
      value: MerchantOrderTypeEnum.WarehouseTransfer,
    },
    { type: 'text', getLabel: () => i18n.t('Order:filterSectionInbound') },
    ...Array.from(INBOUND_ORDER_TYPES).map((t) => ({
      getLabel: () => t,
      value: t,
    })),
    { type: 'text', getLabel: () => i18n.t('Order:filterSectionOther') },
    { getLabel: () => 'Undefined', value: MerchantOrderTypeEnum.Undefined },
  ],
};

const HEALTH_FILTER: FilterDefinition = {
  id: 'health',
  getLabel: () => i18n.t('Order:filterHealth'),
  isDefault: false,
  options: [
    {
      getLabel: () => i18n.t('Order:healthOnTime'),
      value: OpsOrderHealthEnum.OnTime,
    },
    {
      getLabel: () => i18n.t('Order:healthAtRisk'),
      value: OpsOrderHealthEnum.AtRisk,
    },
  ],
};

const COUNTRY_FILTER: FilterDefinition = {
  id: 'country',
  getLabel: () => i18n.t('Order:filterCountry'),
  multiSelect: false,
  options: [
    {
      getLabel: () => i18n.t('Common:Country.DE'),
      value: 'DE',
    },
    {
      getLabel: () => i18n.t('Common:Country.FR'),
      value: 'FR',
    },
    {
      getLabel: () => i18n.t('Common:Country.NL'),
      value: 'NL',
    },
    {
      getLabel: () => i18n.t('Common:Country.BE'),
      value: 'BE',
    },
    {
      getLabel: () => i18n.t('Common:Country.ES'),
      value: 'ES',
    },
    {
      getLabel: () => i18n.t('Common:Country.GB'),
      value: 'GB',
    },
    {
      getLabel: () => i18n.t('Common:Country.IE'),
      value: 'IE',
    },
    {
      getLabel: () => i18n.t('Common:Country.US'),
      value: 'US',
    },
  ],
};

const CARRIER_FILTER: FilterDefinition = {
  id: 'carrier',
  getLabel: () => i18n.t('Order:filterCarrier'),
  options: Object.values(OpsOrderCarrier).map((carrier) => ({
    getLabel: () => carrier,
    value: carrier,
  })),
  multiSelect: false,
};

const WAREHOUSE_FILTER: FilterDefinition = {
  id: 'warehouse',
  getLabel: () => i18n.t('Order:sidebarWarehouse'),
  options: [],
};

// Single-select. Options are loaded on demand via server-side search in
// OrderListView (stores number in the thousands, so there is no flat list to
// preload like warehouses). The selected value is a store's `external_vytal_id`
// (the MerchantStores UUID), which maps to `OpsOrderFilter.store_uuid`.
const STORE_FILTER: FilterDefinition = {
  id: 'store',
  getLabel: () => i18n.t('Order:filterStore'),
  options: [],
};

// Options come from `opsOrderCreators` (populated in OrderListView), i.e. only
// users who have actually created an order. The selected value is an
// AspNetUsers UUID, which maps to `OpsOrderFilter.user_uuid`.
const CREATED_BY_FILTER: FilterDefinition = {
  id: 'createdBy',
  getLabel: () => i18n.t('Order:filterCreatedBy'),
  options: [],
};

const CREATED_AT_FILTER: FilterDefinition = {
  id: 'createdAt',
  kind: 'dateRange',
  getLabel: () => i18n.t('Order:filterCreatedAt'),
  options: [],
};

// ---------------------------------------------------------------------------
// System-specific definitions
// ---------------------------------------------------------------------------

export const getStatusTranslation = (status: MerchantOrderStatusEnum) => {
  return i18n.t(`MerchantOrder:OrderStatus:${status}`) ?? status;
};

const OPEN_SYSTEM_FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'status',
    getLabel: () => i18n.t('Order:filterStatus'),
    isDefault: true,
    isRemovable: false,
    multiSelect: true,
    options: [
      { type: 'text', getLabel: () => i18n.t('Order:filterSectionProcess') },
      {
        getLabel: () =>
          getStatusTranslation(MerchantOrderStatusEnum.OrderReceived),
        value: MerchantOrderStatusEnum.OrderReceived,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Approved),
        value: MerchantOrderStatusEnum.Approved,
      },
      {
        getLabel: () =>
          getStatusTranslation(MerchantOrderStatusEnum.SentToPicking),
        value: MerchantOrderStatusEnum.SentToPicking,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Picked),
        value: MerchantOrderStatusEnum.Picked,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.InTransit),
        value: MerchantOrderStatusEnum.InTransit,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Delivered),
        value: MerchantOrderStatusEnum.Delivered,
      },
      { type: 'text', getLabel: () => i18n.t('Order:filterSectionOther') },
      {
        getLabel: () =>
          getStatusTranslation(MerchantOrderStatusEnum.ReturnToSender),
        value: MerchantOrderStatusEnum.ReturnToSender,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.OnHold),
        value: MerchantOrderStatusEnum.OnHold,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Canceled),
        value: MerchantOrderStatusEnum.Canceled,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Error),
        value: MerchantOrderStatusEnum.Error,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Invalid),
        value: MerchantOrderStatusEnum.Invalid,
      },
    ],
  },
  TYPE_FILTER,
  HEALTH_FILTER,
  COUNTRY_FILTER,
  CARRIER_FILTER,
  WAREHOUSE_FILTER,
  STORE_FILTER,
  CREATED_AT_FILTER,
  CREATED_BY_FILTER,
];

const EVENT_FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'itemLocation',
    getLabel: () => i18n.t('Order:filterItemLocation'),
    isDefault: true,
    isRemovable: false,
    options: [],
  },
  {
    id: 'deliveryAddress',
    getLabel: () => i18n.t('Order:filterDeliveryAddress'),
    isDefault: true,
    isRemovable: false,
    options: [],
  },
  {
    id: 'status',
    getLabel: () => i18n.t('Order:filterStatus'),
    isDefault: true,
    options: [
      { type: 'text', getLabel: () => i18n.t('Order:filterSectionProcess') },
      {
        getLabel: () =>
          getStatusTranslation(MerchantOrderStatusEnum.OrderReceived),
        value: MerchantOrderStatusEnum.OrderReceived,
      },
      {
        getLabel: () =>
          getStatusTranslation(MerchantOrderStatusEnum.PendingApproval),
        value: MerchantOrderStatusEnum.PendingApproval,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Approved),
        value: MerchantOrderStatusEnum.Approved,
      },
      {
        getLabel: () =>
          getStatusTranslation(MerchantOrderStatusEnum.SalesOrderSent),
        value: MerchantOrderStatusEnum.SalesOrderSent,
      },
      {
        getLabel: () =>
          getStatusTranslation(MerchantOrderStatusEnum.SentToPicking),
        value: MerchantOrderStatusEnum.SentToPicking,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Picked),
        value: MerchantOrderStatusEnum.Picked,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.InTransit),
        value: MerchantOrderStatusEnum.InTransit,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Delivered),
        value: MerchantOrderStatusEnum.Delivered,
      },
      { type: 'text', getLabel: () => i18n.t('Order:filterSectionOther') },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.OnHold),
        value: MerchantOrderStatusEnum.OnHold,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Canceled),
        value: MerchantOrderStatusEnum.Canceled,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Error),
        value: MerchantOrderStatusEnum.Error,
      },
      {
        getLabel: () => getStatusTranslation(MerchantOrderStatusEnum.Invalid),
        value: MerchantOrderStatusEnum.Invalid,
      },
    ],
  },
  {
    id: 'eventAccount',
    getLabel: () => i18n.t('Order:filterEventAccount'),
    isDefault: true,
    options: [],
  },
  WAREHOUSE_FILTER,
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns the filter definitions appropriate for the given order system type.
 * Each system type can have its own set of filters, status options, separators,
 * and text headings.
 */
export function getOrderListFilterDefinitions(
  system: OrderSystemType,
): FilterDefinition[] {
  switch (system) {
    case 'EVENT':
      return EVENT_FILTER_DEFINITIONS;
    case 'OPEN_SYSTEM':
    default:
      return OPEN_SYSTEM_FILTER_DEFINITIONS;
  }
}

/**
 * Returns the default active filters derived from the filter definitions
 * for the given system type (i.e. every filter marked `isDefault`).
 */
export function getDefaultOrderListActiveFilters(
  system: OrderSystemType,
): ActiveFilter[] {
  return getOrderListFilterDefinitions(system)
    .filter((f) => f.isDefault)
    .map((f) => ({ id: f.id, value: '__all__' }));
}

/**
 * The persisted active filters that still match `definitions`. A filter with
 * static options keeps only the values that are still options; one whose
 * options are loaded at runtime (e.g. warehouse) is trusted as stored.
 */
export function restoreActiveFilters(
  persisted: unknown,
  definitions: FilterDefinition[],
): ActiveFilter[] {
  if (!Array.isArray(persisted)) return [];
  const restored: ActiveFilter[] = [];
  for (const entry of persisted) {
    if (
      !entry ||
      typeof entry !== 'object' ||
      typeof entry.id !== 'string' ||
      typeof entry.value !== 'string'
    )
      continue;
    const def = definitions.find((f) => f.id === entry.id);
    if (!def) continue;

    const known = new Set(
      def.options.flatMap((o) =>
        !('type' in o) || o.type === 'option' ? [o.value] : [],
      ),
    );
    if (known.size === 0) {
      restored.push({ id: def.id, value: entry.value });
      continue;
    }
    const valid = entry.value
      .split(MULTI_VALUE_SEPARATOR)
      .filter((v: string) => known.has(v));
    restored.push({
      id: def.id,
      value: valid.length > 0 ? valid.join(MULTI_VALUE_SEPARATOR) : '__all__',
    });
  }
  return restored;
}

const ORDER_LIST_SORT_COLUMN_MAP: Record<string, keyof OpsOrderSort> = {
  status: 'status',
  partner: 'store_name',
  createdAt: 'created_at',
  health: 'health',
  country: 'country',
  carrier: 'carrier',
  lastUpdated: 'last_updated',
  createdBy: 'created_by',
  warehouse: 'warehouse',
  eventAccount: 'store_name',
  deliveryDate: 'delivery_date',
  itemLocation: 'item_location',
};

export function buildOrderListFilter(
  activeFilters: ActiveFilter[],
  containerTypeIds: number[],
): OpsOrderFilter | undefined {
  const filter: OpsOrderFilter = {};
  let hasFilter = false;

  if (containerTypeIds.length > 0) {
    filter.container_types_used = containerTypeIds;
    hasFilter = true;
  }

  for (const activeFilter of activeFilters) {
    if (activeFilter.value === '__all__') continue;

    switch (activeFilter.id) {
      case 'status': {
        const values = activeFilter.value.includes(MULTI_VALUE_SEPARATOR)
          ? activeFilter.value.split(MULTI_VALUE_SEPARATOR)
          : [activeFilter.value];
        filter.status = values as MerchantOrderStatusEnum[];
        hasFilter = true;
        break;
      }
      case 'type': {
        const raw = activeFilter.value.includes(MULTI_VALUE_SEPARATOR)
          ? activeFilter.value.split(MULTI_VALUE_SEPARATOR)
          : [activeFilter.value];
        // Expand the synthetic onboarding group into individual enum values.
        const types: MerchantOrderTypeEnum[] = raw.flatMap((v) =>
          v === ONBOARDING_GROUP
            ? ONBOARDING_ENUM_VALUES
            : [v as MerchantOrderTypeEnum],
        );
        filter.type = types;
        hasFilter = true;
        break;
      }
      case 'health':
        filter.health = activeFilter.value as OpsOrderHealthEnum;
        hasFilter = true;
        break;
      case 'country':
        filter.country = activeFilter.value;
        hasFilter = true;
        break;
      case 'carrier':
        filter.carrier = activeFilter.value as OpsOrderCarrier;
        hasFilter = true;
        break;
      case 'warehouse':
        filter.warehouse_uuid = activeFilter.value;
        hasFilter = true;
        break;
      case 'store':
        filter.store_uuid = activeFilter.value;
        hasFilter = true;
        break;
      case 'eventAccount':
        filter.event_account_id = activeFilter.value;
        hasFilter = true;
        break;
      case 'itemLocation':
        filter.item_location_id = activeFilter.value;
        hasFilter = true;
        break;
      case 'deliveryAddress':
        filter.delivery_address_ids = [activeFilter.value];
        hasFilter = true;
        break;
      case 'createdBy':
        filter.user_uuid = activeFilter.value;
        hasFilter = true;
        break;
      case 'createdAt': {
        const range = dateRangeValueToRangeInput(activeFilter.value);
        if (range) {
          filter.created_at = range;
          hasFilter = true;
        }
        break;
      }
    }
  }

  return hasFilter ? filter : undefined;
}

export function buildOrderListSort(
  sorting: SortingState,
): OpsOrderSort | undefined {
  if (sorting.length === 0) return undefined;

  const activeSort = sorting[0];
  const key = ORDER_LIST_SORT_COLUMN_MAP[activeSort.id];
  if (!key) return undefined;

  return { [key]: (activeSort.desc ? 'DESC' : 'ASC') as SortOrder };
}

export function mergeOrderFilters(
  baseFilter?: OpsOrderFilter,
  activeFilter?: OpsOrderFilter,
): OpsOrderFilter | undefined {
  if (!baseFilter && !activeFilter) return undefined;
  return {
    ...(baseFilter ?? {}),
    ...(activeFilter ?? {}),
  };
}
